// === [ CORETEX FYI ] ===
// Keep track if script is loaded
// #CB-A
window.PRODUCT_SCRIPT_LOADED = true;

// ----- [ constants.js
const DEBOUNCE_TIMER_ON_CHANGE = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

// ----- [ pubsub.js
let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    const promises = subscribers[eventName]
      .map((callback) => callback(data))
    return Promise.all(promises);
  } else {
    return Promise.resolve()
  }
}

// ----- [ global.js
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

// document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => { ... }); // #CTL-NN

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

    // (gh) fixing issue #111
    // Code #CB-E
    onButtonClick(event) {
        event.preventDefault();

        // Use .closest() to find the nearest parent button, accounting for clicks on SVG icons or other children.
        const button = event.target.closest('button');
        const action = button.name; // 'plus' or 'minus'
        const previousValue = parseInt(this.input.value);
        const minValue = parseInt(this.input.dataset.min);

        if (action === 'plus') {
            if (previousValue < minValue) {
                this.input.value = minValue;
            } else {
                this.input.stepUp();
            }
        } else if (action === 'minus') {
            this.input.stepDown();
            if (parseInt(this.input.value) < minValue) {
                this.input.value = minValue;
            }
        }

        if (previousValue !== parseInt(this.input.value)) {
            this.input.dispatchEvent(this.changeEvent);
        }
    }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}
customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

//class MenuDrawer extends HTMLElement {} customElements.define('menu-drawer', MenuDrawer); // #CTL-NN
// class HeaderDrawer extends MenuDrawer {} // #CTL-NN
// class ModalDialog extends HTMLElement {} customElements.define('modal-dialog', ModalDialog); // #CTL-NN
// class BulkModal extends HTMLElement {} customElements.define('bulk-modal', BulkModal); // #CTL-NN
// class ModalOpener extends HTMLElement {} customElements.define('modal-opener', ModalOpener); // #CTL-NN

// #CCB-E
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    // Check if there's a <video-player> element
    // If there's a <video-player>, do not run the rest of the logic
    const videoPlayer = this.querySelector('video-player');
    window.pauseAllMedia();

    if (!videoPlayer) {
      const template = this.querySelector('template');
      if (!template) {
        console.error('Template element not found.');
        return;
      }

      let deferredElement;

      if (!this.getAttribute('loaded')) {
        const content = document.createElement('div');
        content.appendChild(template.content.firstElementChild.cloneNode(true));

        this.setAttribute('loaded', true);
        deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
        if (focus) deferredElement.focus();
        if (deferredElement.nodeName === 'VIDEO' && deferredElement.getAttribute('autoplay')) {
          // Force autoplay for Safari
          deferredElement.play();
        }
      } else {
        // If already loaded, query the existing element
        deferredElement = this.querySelector('video, model-viewer, iframe');
      }

      // Workaround for Safari iframe bug (only run if deferredElement exists)
      if (deferredElement) {
        const formerStyle = deferredElement.getAttribute('style');
        deferredElement.setAttribute('style', 'display: block;');
        window.setTimeout(() => {
          deferredElement.setAttribute('style', formerStyle);
        }, 0);
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

// class SliderComponent extends HTMLElement {} customElements.define('slider-component', SliderComponent); // #CTL-NN
// class SlideshowComponent extends SliderComponent { } customElements.define('slideshow-component', SlideshowComponent); // #CTL-NN

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

// former `product-recommendations.js`, now in `global.js`
class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        // if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
        //   this.remove();
        // }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

// class AccountIcon extends HTMLElement { ... } // #CTL-NN
// class BulkAdd extends HTMLElement {} if (!customElements.get('bulk-add')) { customElements.define('bulk-add', BulkAdd); } // #CTL-NN

// ----- [ pickup-availability.js
if (!customElements.get('pickup-availability')) { 
    customElements.define('pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        if (!this.hasAttribute('available')) return;

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.fetchAvailability(this.dataset.variantId);
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=x-pdp-pickup`; // #CTL-E
        // console.log(variantSectionUrl);

        fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (variant?.available) {
          this.fetchAvailability(variant.id);
        } else {
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.innerHTML = '';
        this.appendChild(this.errorHtml);

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        document.body.appendChild(sectionInnerHTML.querySelector('pickup-availability-drawer'));
        /* #CB-NN-CE
        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => {
          document.querySelector('pickup-availability-drawer').classList.add(colorClass);
        });
        */

        const button = this.querySelector('button');
        if (button)
          button.addEventListener('click', (evt) => {
            document.querySelector('pickup-availability-drawer').show(evt.target);
          });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define('pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}

// recipient-form.js
if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          // this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded; // #CTL-NN
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          // this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed; // #CTL-NN
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    }
  );
}

// product-form.js
if (!customElements.get('product-form')) {
	customElements.define(
		'product-form',
		class ProductForm extends HTMLElement {
			constructor() {
				super()

				this.form = this.querySelector('form')
				this.variantIdInput.disabled = false
				this.submitButton = this.querySelector('[type="submit"]')
				this.submitButtonText = this.submitButton.querySelector('span')

                // === [ CORETEX FYI ] ===
			    // === [ Custom Option Validation ] ===
                // #CB-A
				// Only bind validation if custom option inputs exist for this form
				this._hasCustomOptions = this._checkForCustomOptions()
				if (this._hasCustomOptions) {
					// Use capturing phase to intercept submit before ajax-cart-product-form
					this._boundSubmitHandler = this._handleFormSubmit.bind(this)
					this.form.addEventListener('submit', this._boundSubmitHandler, true)
				}
			}

            // === [ CORETEX FYI ] ===
            // === [ Custom Option Validation ] ===
            // #CB-A
            disconnectedCallback() {
				if (this._hasCustomOptions && this._boundSubmitHandler) this.form.removeEventListener('submit', this._boundSubmitHandler, true);
			}

			// Validates required custom option inputs before allowing cart submission
			_checkForCustomOptions() {
				const formId = this.form?.getAttribute('id')
				if (!formId) return false
				return document.querySelectorAll(`.customOptionInput[form="${formId}"]`).length > 0
			}

			_handleFormSubmit(event) {
				const validation = this._validateCustomOptions()

				if (!validation.valid) {
					// Stop the event from reaching ajax-cart-product-form
					event.preventDefault()
					event.stopImmediatePropagation()

					// Show validation errors
					this._showValidationErrors(validation.invalidInputs)
				}
			}

			_validateCustomOptions() {
				const formId = this.form?.getAttribute('id')
				if (!formId) return { valid: true, invalidInputs: [] }

				// Find all custom option inputs that belong to this form
				const customInputs = document.querySelectorAll(`.customOptionInput[form="${formId}"][required]`)
				const invalidInputs = []

				for (const input of customInputs) {
					let isValid = true

					if (input.type === 'checkbox') {
						// Checkbox must be checked if required
						isValid = input.checked
					} else {
						// Text input must have a non-empty value
						isValid = input.value.trim() !== ''
					}

					if (!isValid) invalidInputs.push(input);
				}

				return {
					valid: invalidInputs.length === 0,
					invalidInputs
				}
			}

			_showValidationErrors(invalidInputs) {
				for (const input of invalidInputs) {
					// Add invalid state
					input.setAttribute('aria-invalid', 'true')

					// Focus the first invalid input
					if (input === invalidInputs[0]) {
						input.focus()
						input.scrollIntoView({ behavior: 'smooth', block: 'center' })
					}

					// Remove invalid state on input change
					const removeInvalidState = () => {
						input.removeAttribute('aria-invalid')
						input.removeEventListener('input', removeInvalidState)
						input.removeEventListener('change', removeInvalidState)
					}

					input.addEventListener('input', removeInvalidState)
					input.addEventListener('change', removeInvalidState)
				}
			}

            // === [ CORETEX FYI ] ===
            // #CB-E
            // Added support for preorder attribute
            toggleSubmitButton(disable = true, text) {
                if (disable) {
                    this.submitButton.setAttribute('disabled', 'disabled')
                    if (text) this.submitButtonText.textContent = text
                } else {
                    this.submitButton.removeAttribute('disabled');

                    // Check if the "add to cart" button has preorder="true" attribute
                    const isPreOrder = this.submitButton.getAttribute('preorder') === 'true';

                    if (!isPreOrder) {
                        this.submitButtonText.textContent = window.variantStrings.addToCart
                    } else {
                        this.submitButtonText.textContent = window.variantStrings.preOrder
                    }
                }
            }

			get variantIdInput() {
				return this.form.querySelector('[name=id]')
			}
		}
	)
}

// product-info.js
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // #CB-A: Embla slider
        // (fix) delaying the function execution to ensure the elements are part of the DOM.
        window.addEventListener('load', () => {
            this.handleSlideSelection();
            this.handleStackSelection();
        });
        
      }

      // #CB-A: Embla Slider Initialization
      // Ensures that the appropriate image corresponding to the selected variant is displayed
      // when the page is loaded, by scrolling to the relevant image in the slider.

      // Returns the featured_media.id of the currently selected variant,
      // read from the pre-rendered JSON which is always accurate on page load.
      getSelectedVariantId() {
          const json = this.querySelector('[data-selected-variant]');
          if (json) {
              try {
                  const variant = JSON.parse(json.textContent);
                  return variant?.featured_media?.id ?? null;
              } catch {}
          }
          return null;
      }

      // #CB-A: Embla Slider
      // [On page load when media type is "Slider"]
      // Automatically scrolls the Embla slider to display the image associated with the selected variant.
      handleSlideSelection() {
        const inputVal = this.getSelectedVariantId();
        const slide = this.querySelector(`[thumbs-slide][data-media="${inputVal}"]`);
        if (slide) slide.click();
      }

      // [On page load when media type is "Stacked"]
      // Moves the selected image to the first position in the media gallery.
      handleStackSelection() {
        const inputVal = this.getSelectedVariantId();
        if (inputVal) {
            const mediaId = `${this.dataset.section}-${inputVal}`; // Constructs media ID using section ID and variant attribute `coretex-id`
            this.querySelector('media-gallery')?.setActiveMedia?.(mediaId, true);
        }
      }


      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        // #CB-NN
        // if (!this.dataset.originalSection) {
        //   //UpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        // }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        // UpdateUnsubscriber?.();  // #CTL-NN
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        //productForm?.handleErrorMessage(); // #CTL-NN
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
            //document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks, [
            // Rebind dialog triggers after variant selector is updated
            () => {
              document.querySelectorAll('coretex-dialog').forEach((dialog) => {
                if (typeof dialog.rebindTriggers === 'function') {
                  dialog.rebindTriggers();
                }
              });
            }
          ]);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        // this.querySelector('share-button')?.updateUrl(
        //   `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        // );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const mediaGallery = this.querySelector('media-gallery');
        const mediaGalleryDestinationRoot = html.querySelector('media-gallery');
        if (!mediaGallery || !mediaGalleryDestinationRoot) return;

        const mediaGallerySource = mediaGallery.querySelector('ul[slider-container]');
        const mediaGalleryDestination = mediaGalleryDestinationRoot.querySelector('ul[slider-container]');

        const galleryStateAttributes = ['mq', 'mq-s', 'loop', 'medialess'];
        for (const attribute of galleryStateAttributes) {
          const value = mediaGalleryDestinationRoot.getAttribute(attribute);
          if (value === null) mediaGallery.removeAttribute(attribute);
          else mediaGallery.setAttribute(attribute, value);
        }

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        const thumbnailSource = mediaGallery.querySelector('ul[control-thumbs]');
        const thumbnailDestination = mediaGalleryDestinationRoot.querySelector('ul[control-thumbs]');
        if (thumbnailSource && thumbnailDestination) {
          thumbnailSource.replaceChildren(...Array.from(thumbnailDestination.children));
        }

        const zoomDialog = this.querySelector('coretex-zoom-dialog[id^="imagezoom-"]');
        const zoomDialogDestination = html.querySelector('coretex-zoom-dialog[id^="imagezoom-"]');
        if (zoomDialog && zoomDialogDestination) {
          const zoomLists = ['mini-map ul', 'dialog main ul'];
          for (const selector of zoomLists) {
            const source = zoomDialog.querySelector(selector);
            const destination = zoomDialogDestination.querySelector(selector);
            if (source && destination) source.replaceChildren(...Array.from(destination.children));
          }
          zoomDialog.refresh?.();
        }

        mediaGallery.refresh?.();

        const fallbackMediaId = mediaGallery.querySelector('[data-media-id]')?.dataset.mediaId;
        const mediaId = variantFeaturedMediaId
          ? `${this.dataset.section}-${variantFeaturedMediaId}`
          : fallbackMediaId;
        mediaGallery.selectMedia?.(mediaId);
        zoomDialog?.rebindTriggers?.();
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

// Preview tab
class CoretexPreviewTab {
  constructor() {
    this.cache = new Map();
    this.isExpanded = false;

    // Cache DOM elements with early returns
    this.tab = document.getElementById('previewTab');
    if (!this.tab) return;

    this.productForm = document.querySelector('.etheryx-product-options product-form');
    if (!this.productForm) return;

    this.productOptions = document.querySelector('.etheryx-product-options');
    if (!this.productOptions) return;

    // Set initial preview-tab attribute on body
    this._updateBodyAttribute();

    this._init();
  }

  _init() {
    this._setupIntersectionObserver();
    this._setupEventListeners();
  }

  _setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!_isMobileViewport()) continue;
        
        entry.isIntersecting ? this._hideTab() : this._showTabIfNotExpanded();
      }
    }, {
      rootMargin: '0px 0px 50px 0px'
    });
    
    observer.observe(this.productForm);
    this.cache.set('observer', observer);
  }

  _setupEventListeners() {
    // Tab click to expand actions
    this.tab.addEventListener('click', () => this.expandActions());

    // Close button click to collapse actions
    const closeButton = document.querySelector('button[preview-tab-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.collapseActions());
    }

    // Click outside to dismiss
    document.addEventListener('click', this._handleOutsideClick.bind(this));

    // Handle window resize with debouncing
    window.addEventListener('resize', _debounce(() => {
      if (!_isMobileViewport() && this.isExpanded) this.collapseActions();
    }, 250));

  }

  _handleOutsideClick(e) {
    if (!this.isExpanded) return;

    // Ignore synthetic events and events without a target
    if (!e.target || !e.isTrusted) return;

    // Ignore clicks on variant selectors and their children (they might be transitioning)
    if (e.target.closest('variant-selects') || e.target.closest('[data-variant-picker]')) return;

    const isClickInside = this.productOptions.contains(e.target) || this.tab.contains(e.target);
    if (!isClickInside) this.collapseActions();
  }

  _showTabIfNotExpanded() {
    if (!this.isExpanded) this._showTab();
  }

  _showTab() {
    if (!_isMobileViewport()) return;

    this.tab.classList.add('show');
    this._updateBodyAttribute();
    this._dispatchEvent('tab:show');
  }

  _hideTab() {
    this.tab.classList.remove('show');
    this._updateBodyAttribute();
    this._dispatchEvent('tab:hide');
  }

  expandActions() {
    this.isExpanded = true;
    this._hideTab();
    this.productOptions.setAttribute('data-mobile-state', 'expanded');

    // Prevent body scroll when expanded
    document.body.style.overflow = 'hidden';
    this._updateBodyAttribute();
    this._dispatchEvent('actions:expanded');
  }

  async collapseActions() {
    this.isExpanded = false;
    
    // Set closing state for animation
    this.productOptions.setAttribute('data-mobile-state', 'closing');
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Wait for animation to complete
    await this._waitForTransition();
    
    this.productOptions.removeAttribute('data-mobile-state');

    // Show tab again if actions are not visible
    if (this._shouldShowTab()) this._showTab();

    this._updateBodyAttribute();
    this._dispatchEvent('actions:collapsed');
  }

  _waitForTransition() {
    return new Promise((resolve) => {
      const handleTransitionEnd = () => {
        this.productOptions.removeEventListener('transitionend', handleTransitionEnd);
        resolve();
      };
      
      this.productOptions.addEventListener('transitionend', handleTransitionEnd);
      
      // Fallback timeout in case transition doesn't fire
      setTimeout(resolve, 350);
    });
  }

  _shouldShowTab() {
    const rect = this.productForm.getBoundingClientRect();
    const isVisible = rect.bottom > window.innerHeight - 50;
    
    return !isVisible && _isMobileViewport();
  }

  _updateBodyAttribute() {
    const hasTab = this.tab && this.tab.classList.contains('show');
    const isExpanded = this.isExpanded;

    if (isExpanded) {
      document.body.setAttribute('preview-tab', 'expanded');
    } else if (hasTab) {
      document.body.setAttribute('preview-tab', 'visible');
    } else {
      document.body.removeAttribute('preview-tab');
    }
  }

  _dispatchEvent(eventName) {
    const event = new CustomEvent(eventName, {
      detail: { component: this, isExpanded: this.isExpanded }
    });
    document.dispatchEvent(event);
  }

  // Cleanup method
  disconnect() {
    const observer = this.cache.get('observer');
    observer?.disconnect();
    this.cache.clear();

    // Clean up body attribute on disconnect
    document.body.removeAttribute('preview-tab');
  }
}

// Module-scoped utility functions
const _isMobileViewport = () => window.innerWidth <= 777;

const _debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => { clearTimeout(timeout); func(...args); };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => { new CoretexPreviewTab(); });
